import Box from "@amzn/meridian/box";
import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Icon from "@amzn/meridian/icon";
import Modal from "@amzn/meridian/modal";
import Responsive from "@amzn/meridian/responsive";
import Row from "@amzn/meridian/row";
import Sheet from "@amzn/meridian/sheet";
import Text from "@amzn/meridian/text";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import copyTokens from "@amzn/meridian-tokens/base/icon/copy";
import React from "react";

import FacebookIcon from "../../assests/Icons/FacebookIcon";
import MailIcon from "../../assests/Icons/MailIcon";
import TwitterIcon from "../../assests/Icons/TwitterIcon";
import WhatsappIcon from "../../assests/Icons/WhatsappIcon";
import { MixPanelEventName } from "../../constants/MixPanelEventName";
import { ScreenSize } from "../../constants/ScreenSize";
import { SocialMediaInfo } from "../../constants/SocialMediaList";
import { commonUnityStore } from "../../store/CommonUnityStore";
import { buttonStyle } from "../../styles/Style";
import { ISocialMediaModalProps } from "../../types/SocilaMedia.types";
import MixPanel from "../../utils/MixPanelConfig";
import { Links } from "../../utils/MixpanelLinks";

const {
  COPY_LINK,
  SUCCESS,
  COPIED_SUCCESSFULLY,
  FACEBOOK,
  MAIL,
  TWITTER,
  WHATSAPP,
  CONTACT_US_PREFIX,
  CONTACT_US_SUFFIX,
  NAMASTE,
  ORDER_TEXT_PREFIX,
  ORDER_TEXT_SUFFIX,
  THANK_YOU,
} = SocialMediaInfo;

const SocialMediaModal = ({
  isSocialMediaLinkOpen,
  handleCloseSocialMediaLink,
  domainLink:link,
  shareModalTitle,
  mobileNumber,
  storeName,
  isStore = true,
  productName,
}: ISocialMediaModalProps) => {
  const { handleOpenToaster, toasterData } = commonUnityStore;

  const {
    SHARE_WHATSAPP_CLICK,
    SHARE_MAIL_CLICK,
    SHARE_FACEBOOK_CLICK,
    SHARE_X_CLICK,
    SHARE_STORE_FACEBOOK_CLICK,
    SHARE_STORE_MAIL_CLICK,
    SHARE_STORE_WHATSAPP_CLICK,
    SHARE_STORE_X_CLICK,
  } = MixPanelEventName;

  const { FACEBOOK_URL, MAIL_URL, WHATSAPP_URL, X_URL } = Links;

  const socialMediaPlatformFunctions = {
    [FACEBOOK]: () => {
      MixPanel.track(
        isStore ? SHARE_STORE_FACEBOOK_CLICK : SHARE_FACEBOOK_CLICK,
        isStore ? { store_url: link } : { "product name": productName }
      );
      window.open(`${FACEBOOK_URL}${encodeURIComponent(link)}`, "_blank");
    },
    [WHATSAPP]: () => {
      MixPanel.track(
        isStore ? SHARE_STORE_WHATSAPP_CLICK : SHARE_WHATSAPP_CLICK,
        isStore ? { store_url: link } : { "product name": productName }
      );
    },
    [MAIL]: () => {
      MixPanel.track(
        isStore ? SHARE_STORE_MAIL_CLICK : SHARE_MAIL_CLICK,
        isStore ? { store_url: link } : { "product name": productName }
      );

      const subject = encodeURIComponent("New Arrival Just In - Limited Stock!");

      const body = encodeURIComponent(
        `Hi,\n\nWe just launched something we think you’ll love.\n\nCheck it out here 👉 ${link}\n\nLet us know what you think!\n\nBest,`
      );

      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    },
    [TWITTER]: () => {
      MixPanel.track(
        isStore ? SHARE_STORE_X_CLICK : SHARE_X_CLICK,
        isStore ? { store_url: link } : { "product name": productName }
      );
      window.open(`${X_URL}${encodeURIComponent(link)}`, "_blank");
    },
  };
  
  const handleCopyLink = (): void => {
    navigator.clipboard.writeText(link);
    handleOpenToaster({
      ...toasterData,
      type: SUCCESS,
      text: COPIED_SUCCESSFULLY,
    });
    handleCloseSocialMediaLink();
  };


  const onWhatsAppClick = () => {
    const whatsappText = `${NAMASTE}\n\n${ORDER_TEXT_PREFIX} ${storeName} ${ORDER_TEXT_SUFFIX} ${link}\n\n${CONTACT_US_PREFIX} ${mobileNumber} ${CONTACT_US_SUFFIX}\n\n${THANK_YOU}`;
    const whatsappUrl = `${WHATSAPP_URL}${encodeURIComponent(whatsappText)}`;
    window.open(whatsappUrl, "_blank");
    socialMediaPlatformFunctions[WHATSAPP]();
  };

  const handleViewSocialMedia = (showMobileView: boolean): React.JSX.Element => {
    return (
      <>
        <Text type="h400" data-testid="socialmediamodal-title-text">{shareModalTitle}</Text>
        <Column spacingInset="300 0 0 200" alignmentVertical="justify" data-testid="socialmediamodal-content-column">
          <Row width="100%" alignmentHorizontal="justify" spacing="300" data-testid="socialmediamodal-buttons-row">
            <Button onClick={() => socialMediaPlatformFunctions[FACEBOOK]()} className={buttonStyle} data-testid="socialmediamodal-facebook-button">
              <Row alignmentHorizontal="start" spacingInset="0 100 100 0" spacing="200" data-testid="socialmediamodal-facebook-content-row">
                <FacebookIcon />
                <Text data-testid="socialmediamodal-facebook-text">{FACEBOOK}</Text>
              </Row>
            </Button>
            <Button onClick={onWhatsAppClick} className={buttonStyle} data-testid="socialmediamodal-whatsapp-button">
              <Row alignmentHorizontal="start" spacingInset="0 100 100 0" spacing="200" data-testid="socialmediamodal-whatsapp-content-row">
                <WhatsappIcon />
                <Text data-testid="socialmediamodal-whatsapp-text">{WHATSAPP}</Text>
              </Row>
            </Button>
            <Button onClick={() => socialMediaPlatformFunctions[MAIL]()} className={buttonStyle} data-testid="socialmediamodal-mail-button">
              <Row alignmentHorizontal="start" spacingInset="0 100 100 0" spacing="200" data-testid="socialmediamodal-mail-content-row">
                <MailIcon />
                <Text data-testid="socialmediamodal-mail-text">{MAIL}</Text>
              </Row>
            </Button>
            <Button onClick={() => socialMediaPlatformFunctions[TWITTER]()} className={buttonStyle} data-testid="socialmediamodal-twitter-button">
              <Row alignmentHorizontal="start" spacingInset="0 100 100 0" spacing="200" data-testid="socialmediamodal-twitter-content-row">
                <TwitterIcon />
                <Text data-testid="socialmediamodal-twitter-text">{TWITTER}</Text>
              </Row>
            </Button>
          </Row>
          <Column spacing="200" spacingInset="none" data-testid="socialmediamodal-copylink-column">
            <Text data-testid="socialmediamodal-copylink-label">{COPY_LINK}</Text>
            <Box width="100%" type="outline" spacingInset="200 200 200 300" data-testid="socialmediamodal-copylink-box">
              <Row width="100%" spacingInset="none" data-testid="socialmediamodal-copylink-row">
                <Box width="100%" overflowY="auto" data-testid="socialmediamodal-link-box">
                  <Text data-testid="socialmediamodal-link-text">{link}</Text>
                </Box>
                <Button
                  minWidth="20px"
                  type="secondary"
                  onClick={handleCopyLink}
                  data-testid="socialmediamodal-copy-button"
                >
                  <Icon tokens={copyTokens} data-testid="socialmediamodal-copy-icon"/>
                </Button>
              </Row>
            </Box>
          </Column>
          {showMobileView && <Column height="145px" data-testid="socialmediamodal-mobile-spacer"/>}
        </Column>
      </>
    );
  };

  return (
    <Responsive
      query="max-width"
      props={{
        showMobileView: {
          default: false,
          [ScreenSize._720]: true,
        },
      }}
    >
      {({ showMobileView }) =>
        !showMobileView ? (
          <Modal
            data-testid="socialmediamodal-desktop-modal"
            width="580px"
            open={isSocialMediaLinkOpen}
            onClose={handleCloseSocialMediaLink}
            scrollContainer="viewport"
            closeLabel="Close"
          >
            {handleViewSocialMedia(showMobileView)}
          </Modal>
        ) : (
          <Sheet
            data-testid="socialmediamodal-mobile-sheet"
            type="overlay"
            side="bottom"
            open={isSocialMediaLinkOpen}
            onClose={handleCloseSocialMediaLink}
          >
            <Row alignmentHorizontal="end" width="100%" data-testid="socialmediamodal-close-row">
              <Button onClick={handleCloseSocialMediaLink} type="icon" data-testid="socialmediamodal-close-button">
                <Icon tokens={closeSmallTokens} data-testid="socialmediamodal-close-icon"/>
              </Button>
            </Row>
            {handleViewSocialMedia(showMobileView)}
          </Sheet>
        )
      }
    </Responsive>
  );
};

export default SocialMediaModal;
